import { useCallback, useSyncExternalStore } from 'react';
import type { Speicher } from '../verortung/sitzung';
import type { MerkSchluessel } from './typen';

/**
 * „Zuletzt“ und Favoriten der Katalogauswahl — je Gerät und Nutzer im
 * localStorage (Muster verortung/geraetEinstellung.ts). Nichts wirft:
 * fehlender/voller/kaputter Speicher heißt nur „leere Liste“ bzw. „gilt bis
 * zum Neuladen“.
 */

export const ZULETZT_MAX = 12;
/** Weiche Obergrenze, damit die Liste nicht unbegrenzt wächst. */
export const FAVORITEN_MAX = 100;

/** Eigenes Ereignis: gleiche Seite (der `storage`-Event kommt nur in ANDEREN Tabs an). */
const EREIGNIS = 'sigeko:mangelKatalogMerkliste';

interface MerkStand {
    zuletzt: MerkSchluessel[];
    favoriten: MerkSchluessel[];
}

export function merklisteSchluessel(nutzerId: string | null): string {
    const id = typeof nutzerId === 'string' && nutzerId.trim() ? nutzerId.trim() : 'anon';
    return `sigeko.mangelKatalog.${id}.v1`;
}

const standardSpeicher = (): Speicher | null => {
    try {
        return typeof localStorage !== 'undefined' ? localStorage : null;
    } catch {
        return null;
    }
};

const gueltig = (s: unknown): s is MerkSchluessel =>
    typeof s === 'string' && s.length <= 200 && /^(knoten|eintrag):\S/.test(s);

/** Frühere Fassung merkte Einträge des Gesamtkatalogs als `bg:<id>` — gleiche ID, neues Präfix (Codex-Review 28.09.). */
const altPraefixUmstellen = (s: unknown): unknown =>
    typeof s === 'string' && s.startsWith('bg:') ? `eintrag:${s.slice(3)}` : s;

const listeLesen = (roh: unknown, max: number): MerkSchluessel[] => {
    if (!Array.isArray(roh)) return [];
    return [...new Set(roh.map(altPraefixUmstellen).filter(gueltig))].slice(0, max);
};

function standNormalisieren(roh: unknown): MerkStand {
    if (!roh || typeof roh !== 'object' || Array.isArray(roh)) return { zuletzt: [], favoriten: [] };
    const o = roh as Record<string, unknown>;
    return { zuletzt: listeLesen(o.zuletzt, ZULETZT_MAX), favoriten: listeLesen(o.favoriten, FAVORITEN_MAX) };
}

function standLaden(schluessel: string, speicher: Speicher | null): MerkStand {
    if (!speicher) return { zuletzt: [], favoriten: [] };
    try {
        const text = speicher.getItem(schluessel);
        return text === null ? { zuletzt: [], favoriten: [] } : standNormalisieren(JSON.parse(text));
    } catch {
        return { zuletzt: [], favoriten: [] };
    }
}

function standSpeichern(schluessel: string, stand: MerkStand, speicher: Speicher | null): void {
    if (!speicher) return;
    try {
        speicher.setItem(schluessel, JSON.stringify(stand));
    } catch {
        // Nur Komfort.
    }
}

// Stand je Schlüssel im Speicher der Seite (für den Hook, stabile Referenzen).
const staende = new Map<string, MerkStand>();

function schnappschuss(schluessel: string): MerkStand {
    let s = staende.get(schluessel);
    if (!s) {
        s = standLaden(schluessel, standardSpeicher());
        staende.set(schluessel, s);
    }
    return s;
}

function melden(schluessel: string, stand: MerkStand): void {
    try {
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function' && typeof CustomEvent === 'function') {
            window.dispatchEvent(new CustomEvent(EREIGNIS, { detail: { schluessel, stand } }));
        }
    } catch {
        // Nur Komfort.
    }
}

/**
 * Liest, ändert und schreibt. Ohne ausdrücklichen Speicher (Normalfall)
 * zählt der Stand der Seite und alle Hook-Nutzer werden benachrichtigt.
 */
function aendern(
    nutzerId: string | null,
    speicher: Speicher | null | undefined,
    f: (s: MerkStand) => MerkStand,
): MerkStand {
    const schluessel = merklisteSchluessel(nutzerId);
    const standard = speicher === undefined;
    const sp = standard ? standardSpeicher() : speicher;
    const alt = standard ? schnappschuss(schluessel) : standLaden(schluessel, sp);
    const neu = f(alt);
    if (neu === alt) return alt;
    standSpeichern(schluessel, neu, sp);
    if (standard) {
        staende.set(schluessel, neu);
        melden(schluessel, neu);
    }
    return neu;
}

export function zuletztLesen(nutzerId: string | null, speicher?: Speicher | null): MerkSchluessel[] {
    const schluessel = merklisteSchluessel(nutzerId);
    const s = speicher === undefined ? schnappschuss(schluessel) : standLaden(schluessel, speicher);
    return [...s.zuletzt];
}

export function favoritenLesen(nutzerId: string | null, speicher?: Speicher | null): MerkSchluessel[] {
    const schluessel = merklisteSchluessel(nutzerId);
    const s = speicher === undefined ? schnappschuss(schluessel) : standLaden(schluessel, speicher);
    return [...s.favoriten];
}

/** Setzt `s` an den Anfang von „Zuletzt“ (ohne Doppelte, höchstens ZULETZT_MAX). */
export function zuletztMerken(nutzerId: string | null, s: MerkSchluessel, speicher?: Speicher | null): MerkSchluessel[] {
    if (!gueltig(s)) return zuletztLesen(nutzerId, speicher);
    const neu = aendern(nutzerId, speicher, (alt) => {
        if (alt.zuletzt[0] === s) return alt;
        return { ...alt, zuletzt: [s, ...alt.zuletzt.filter((x) => x !== s)].slice(0, ZULETZT_MAX) };
    });
    return [...neu.zuletzt];
}

/** Stern an/aus; neue Favoriten kommen nach vorn. */
export function favoritUmschalten(nutzerId: string | null, s: MerkSchluessel, speicher?: Speicher | null): MerkSchluessel[] {
    if (!gueltig(s)) return favoritenLesen(nutzerId, speicher);
    const neu = aendern(nutzerId, speicher, (alt) => ({
        ...alt,
        favoriten: alt.favoriten.includes(s)
            ? alt.favoriten.filter((x) => x !== s)
            : [s, ...alt.favoriten].slice(0, FAVORITEN_MAX),
    }));
    return [...neu.favoriten];
}

// ─── React-Hook ──────────────────────────────────────────────────────────────

function abonnieren(schluessel: string, rueckruf: () => void): () => void {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return () => undefined;
    const eigenes = (e: Event) => {
        const detail = (e as CustomEvent<unknown>).detail;
        if (!detail || typeof detail !== 'object' || (detail as { schluessel?: unknown }).schluessel !== schluessel) return;
        rueckruf();
    };
    const fremd = (e: StorageEvent) => {
        if (e.key !== null && e.key !== schluessel) return;
        staende.set(schluessel, standLaden(schluessel, standardSpeicher()));
        rueckruf();
    };
    window.addEventListener(EREIGNIS, eigenes);
    window.addEventListener('storage', fremd);
    return () => {
        window.removeEventListener(EREIGNIS, eigenes);
        window.removeEventListener('storage', fremd);
    };
}

/** Zuletzt/Favoriten des Nutzers samt Setzern; Änderungen erreichen alle Hook-Nutzer der Seite. */
export function useMerkliste(nutzerId: string | null): {
    zuletzt: MerkSchluessel[];
    favoriten: MerkSchluessel[];
    merken: (s: MerkSchluessel) => void;
    favoritUmschalten: (s: MerkSchluessel) => void;
} {
    const schluessel = merklisteSchluessel(nutzerId);
    const abo = useCallback((rueckruf: () => void) => abonnieren(schluessel, rueckruf), [schluessel]);
    const lesen = useCallback(() => schnappschuss(schluessel), [schluessel]);
    const stand = useSyncExternalStore(abo, lesen, lesen);
    const merken = useCallback((s: MerkSchluessel) => {
        zuletztMerken(nutzerId, s);
    }, [nutzerId]);
    const umschalten = useCallback((s: MerkSchluessel) => {
        favoritUmschalten(nutzerId, s);
    }, [nutzerId]);
    return { zuletzt: stand.zuletzt, favoriten: stand.favoriten, merken, favoritUmschalten: umschalten };
}
